"use client"

import { useState, useEffect } from "react"
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Power,
  PowerOff,
  ExternalLink,
  Copy,
  CheckCircle,
  Building2,
  Users,
  Target,
  ArrowDown,
  GitBranch,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"

interface VendorIntakeForm {
  id: string
  alias: string // PUBLIC VIEW FOR SALES REPS
  formstackUrl: string // INTERNAL USE ONLY
  lawFirmName: string // INTERNAL USE ONLY
  clientEligibility: string // age, game system, qualifying titles
  maxClientsPerIntake: number
  priority: number // Priority of fulfillment
  campaignAssignment: string
  bonusPerQualifiedSale: number
  isActive: boolean
  isHidden: boolean
  createdAt: string
  updatedAt: string
  assignedOrganizations?: string[]
  totalAssignments?: number
  activeAssignments?: number
}

export default function FMOVendorIntakeForms() {
  const [forms, setForms] = useState<VendorIntakeForm[]>([
    {
      id: "form-001",
      alias: "Mass Tort Gaming Addiction A",
      formstackUrl: "https://formstack.com/forms/gaming-addiction-intake-a",
      lawFirmName: "Smith & Associates Law Firm",
      clientEligibility: "Ages 18-65, PlayStation/Xbox/PC, FIFA/Madden/NBA2K series, minimum 2 hours daily play",
      maxClientsPerIntake: 5,
      priority: 1,
      campaignAssignment: "Mass Tort Gaming Campaign 2024",
      bonusPerQualifiedSale: 150.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-15T10:00:00Z",
      updatedAt: "2024-01-20T14:30:00Z",
      assignedOrganizations: ["org-em-001", "org-dm-001"],
      totalAssignments: 5,
      activeAssignments: 4,
    },
    {
      id: "form-002",
      alias: "Medicare Advantage Enrollment",
      formstackUrl: "https://formstack.com/forms/medicare-advantage-intake",
      lawFirmName: "Johnson Legal Group",
      clientEligibility: "Ages 65+, Medicare eligible, residing in target states (FL, TX, CA, NY)",
      maxClientsPerIntake: 3,
      priority: 2,
      campaignAssignment: "Medicare Advantage 2024",
      bonusPerQualifiedSale: 200.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-10T09:00:00Z",
      updatedAt: "2024-01-18T16:45:00Z",
      assignedOrganizations: ["org-em-002", "org-cm-001"],
      totalAssignments: 3,
      activeAssignments: 3,
    },
    {
      id: "form-003",
      alias: "Life Insurance Application",
      formstackUrl: "https://formstack.com/forms/life-insurance-comprehensive",
      lawFirmName: "Williams & Partners",
      clientEligibility: "Ages 25-70, good health status, minimum $50k annual income, non-smoker preferred",
      maxClientsPerIntake: 2,
      priority: 3,
      campaignAssignment: "Life Insurance Premium Campaign",
      bonusPerQualifiedSale: 300.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-05T11:30:00Z",
      updatedAt: "2024-01-22T13:15:00Z",
      assignedOrganizations: ["org-dm-002"],
      totalAssignments: 2,
      activeAssignments: 1,
    },
    {
      id: "form-004",
      alias: "Dental Coverage Intake",
      formstackUrl: "https://formstack.com/forms/dental-coverage-enrollment",
      lawFirmName: "Brown Legal Services",
      clientEligibility: "Ages 18-80, no current dental coverage, family size 1-6 members",
      maxClientsPerIntake: 4,
      priority: 4,
      campaignAssignment: "Dental Coverage Expansion",
      bonusPerQualifiedSale: 75.0,
      isActive: false,
      isHidden: true,
      createdAt: "2024-01-12T08:45:00Z",
      updatedAt: "2024-01-25T10:20:00Z",
      assignedOrganizations: [],
      totalAssignments: 0,
      activeAssignments: 0,
    },
    {
      id: "form-005",
      alias: "Auto Insurance Claims",
      formstackUrl: "https://formstack.com/forms/auto-insurance-claims-intake",
      lawFirmName: "Davis & Thompson Law",
      clientEligibility: "Ages 21-75, valid driver's license, recent accident within 30 days, not at fault",
      maxClientsPerIntake: 6,
      priority: 2,
      campaignAssignment: "Auto Claims Recovery 2024",
      bonusPerQualifiedSale: 125.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-08T14:20:00Z",
      updatedAt: "2024-01-19T11:10:00Z",
      assignedOrganizations: ["org-em-001", "org-dm-001", "org-cm-002"],
      totalAssignments: 7,
      activeAssignments: 6,
    },
    {
      id: "form-006",
      alias: "Workers Compensation Claims",
      formstackUrl: "https://formstack.com/forms/workers-comp-intake",
      lawFirmName: "Miller & Associates",
      clientEligibility: "Ages 18-67, workplace injury within 90 days, employed at time of injury",
      maxClientsPerIntake: 3,
      priority: 1,
      campaignAssignment: "Workers Comp Recovery 2024",
      bonusPerQualifiedSale: 175.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-20T09:15:00Z",
      updatedAt: "2024-01-25T11:30:00Z",
      assignedOrganizations: ["org-em-001"],
      totalAssignments: 2,
      activeAssignments: 2,
    },
    {
      id: "form-007",
      alias: "Personal Injury Consultation",
      formstackUrl: "https://formstack.com/forms/personal-injury-intake",
      lawFirmName: "Roberts Law Firm",
      clientEligibility: "Ages 18-80, injury within 2 years, not at fault, medical treatment received",
      maxClientsPerIntake: 4,
      priority: 2,
      campaignAssignment: "Personal Injury Campaign 2024",
      bonusPerQualifiedSale: 225.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-18T14:45:00Z",
      updatedAt: "2024-01-26T16:20:00Z",
      assignedOrganizations: ["org-dm-001", "org-cm-001"],
      totalAssignments: 4,
      activeAssignments: 3,
    },
    {
      id: "form-008",
      alias: "Social Security Disability",
      formstackUrl: "https://formstack.com/forms/ssdi-intake",
      lawFirmName: "Thompson Legal Services",
      clientEligibility: "Ages 18-65, unable to work for 12+ months, medical documentation available",
      maxClientsPerIntake: 2,
      priority: 3,
      campaignAssignment: "SSDI Assistance Program",
      bonusPerQualifiedSale: 100.0,
      isActive: false,
      isHidden: false,
      createdAt: "2024-01-22T10:30:00Z",
      updatedAt: "2024-01-27T12:45:00Z",
      assignedOrganizations: [],
      totalAssignments: 0,
      activeAssignments: 0,
    },
    {
      id: "form-009",
      alias: "Bankruptcy Consultation",
      formstackUrl: "https://formstack.com/forms/bankruptcy-intake",
      lawFirmName: "Anderson & Partners",
      clientEligibility: "Ages 21-75, debt over $10k, income below median, no recent bankruptcy filing",
      maxClientsPerIntake: 3,
      priority: 4,
      campaignAssignment: "Debt Relief Campaign",
      bonusPerQualifiedSale: 125.0,
      isActive: true,
      isHidden: true,
      createdAt: "2024-01-25T13:20:00Z",
      updatedAt: "2024-01-28T15:10:00Z",
      assignedOrganizations: ["org-cm-002"],
      totalAssignments: 1,
      activeAssignments: 1,
    },
    {
      id: "form-010",
      alias: "Tax Resolution Services",
      formstackUrl: "https://formstack.com/forms/tax-resolution-intake",
      lawFirmName: "Wilson Tax Law",
      clientEligibility: "Ages 25-70, IRS debt over $5k, no current payment plan, tax returns filed",
      maxClientsPerIntake: 5,
      priority: 2,
      campaignAssignment: "Tax Relief 2024",
      bonusPerQualifiedSale: 200.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-01-28T11:00:00Z",
      updatedAt: "2024-01-30T14:25:00Z",
      assignedOrganizations: ["org-em-002", "org-dm-001"],
      totalAssignments: 3,
      activeAssignments: 2,
    },
    {
      id: "form-011",
      alias: "Employment Law Consultation",
      formstackUrl: "https://formstack.com/forms/employment-law-intake",
      lawFirmName: "Garcia Employment Law",
      clientEligibility: "Ages 18-67, workplace discrimination/harassment, incident within 180 days",
      maxClientsPerIntake: 2,
      priority: 1,
      campaignAssignment: "Employment Rights Campaign",
      bonusPerQualifiedSale: 250.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-02-01T09:30:00Z",
      updatedAt: "2024-02-03T11:15:00Z",
      assignedOrganizations: ["org-em-001", "org-dm-002"],
      totalAssignments: 3,
      activeAssignments: 3,
    },
    {
      id: "form-012",
      alias: "Medical Malpractice Review",
      formstackUrl: "https://formstack.com/forms/malpractice-intake",
      lawFirmName: "Henderson Medical Law",
      clientEligibility:
        "Ages 18-85, medical treatment complications, incident within 2 years, medical records available",
      maxClientsPerIntake: 1,
      priority: 1,
      campaignAssignment: "Medical Malpractice 2024",
      bonusPerQualifiedSale: 400.0,
      isActive: true,
      isHidden: false,
      createdAt: "2024-02-05T15:45:00Z",
      updatedAt: "2024-02-07T17:30:00Z",
      assignedOrganizations: ["org-em-002"],
      totalAssignments: 1,
      activeAssignments: 1,
    },
  ])

  const [loading, setLoading] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [showAssignForm, setShowAssignForm] = useState(false)
  const [selectedForm, setSelectedForm] = useState<VendorIntakeForm | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all")
  const [priorityFilter, setPriorityFilter] = useState<"all" | "1" | "2" | "3" | "4">("all")

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const [newForm, setNewForm] = useState({
    alias: "",
    formstackUrl: "",
    lawFirmName: "",
    clientEligibility: "",
    maxClientsPerIntake: 1,
    priority: 1,
    campaignAssignment: "",
    bonusPerQualifiedSale: 0,
  })

  // Mock organizations data
  const [organizations] = useState([
    {
      id: "org_1",
      name: "Eastern Region EM",
      type: "EM",
      managerId: "mgr_001",
      managerName: "John Smith",
      isActive: true,
      agentCount: 156,
      children: [
        {
          id: "org_2",
          name: "Northeast DM Team A",
          type: "DM",
          agentCount: 78,
          children: [
            { id: "org_4", name: "Boston CM Branch", type: "CM", agentCount: 42 },
            { id: "org_5", name: "Hartford CM Branch", type: "CM", agentCount: 36 },
          ],
        },
        {
          id: "org_3",
          name: "Southeast DM Team B",
          type: "DM",
          agentCount: 78,
          children: [{ id: "org_6", name: "Atlanta CM Branch", type: "CM", agentCount: 78 }],
        },
      ],
    },
    {
      id: "org_7",
      name: "Western Region EM",
      type: "EM",
      managerId: "mgr_007",
      managerName: "Robert Taylor",
      isActive: true,
      agentCount: 142,
      children: [
        {
          id: "org_8",
          name: "Pacific DM Team C",
          type: "DM",
          agentCount: 142,
          children: [
            { id: "org_9", name: "Los Angeles CM Branch", type: "CM", agentCount: 70 },
            { id: "org_10", name: "San Francisco CM Branch", type: "CM", agentCount: 72 },
          ],
        },
      ],
    },
  ])

  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([])
  const [assignmentPreview, setAssignmentPreview] = useState<{
    directUsers: number
    inheritedUsers: number
    totalUsers: number
    affectedOrganizations: string[]
  }>({
    directUsers: 0,
    inheritedUsers: 0,
    totalUsers: 0,
    affectedOrganizations: [],
  })

  useEffect(() => {
    calculateAssignmentPreview()
  }, [selectedOrganizations])

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter, priorityFilter, itemsPerPage])

  const calculateAssignmentPreview = () => {
    let directUsers = 0
    let inheritedUsers = 0
    const affectedOrganizations: string[] = []

    const calculateForOrg = (org: any, isSelected: boolean) => {
      if (isSelected) {
        affectedOrganizations.push(org.name)
        if (org.type === "EM" || org.type === "DM" || org.type === "CM") {
          directUsers += 0 // Managers don't directly use forms
        }
      }

      if (org.children) {
        org.children.forEach((child: any) => {
          if (isSelected || selectedOrganizations.includes(child.id)) {
            affectedOrganizations.push(child.name)
            if (child.type === "CM") {
              inheritedUsers += child.agentCount
            } else if (child.type === "BM" || child.type === "AM" || child.type === "M") {
              inheritedUsers += child.agentCount
            }
            calculateForOrg(child, isSelected || selectedOrganizations.includes(child.id))
          } else {
            calculateForOrg(child, false)
          }
        })
      }
    }

    organizations.forEach((org) => {
      calculateForOrg(org, selectedOrganizations.includes(org.id))
    })

    setAssignmentPreview({
      directUsers,
      inheritedUsers,
      totalUsers: directUsers + inheritedUsers,
      affectedOrganizations: Array.from(new Set(affectedOrganizations)),
    })
  }

  const handleCreateForm = async () => {
    try {
      setLoading(true)
      const createdForm: VendorIntakeForm = {
        ...newForm,
        id: `form-${Date.now()}`,
        isActive: true,
        isHidden: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        assignedOrganizations: [],
        totalAssignments: 0,
        activeAssignments: 0,
      }
      setForms([...forms, createdForm])
      setShowCreateForm(false)
      resetForm()
    } catch (error) {
      console.error("Error creating form:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleEditForm = async () => {
    if (!selectedForm) return

    try {
      setLoading(true)
      const updatedForm = {
        ...selectedForm,
        ...newForm,
        updatedAt: new Date().toISOString(),
      }
      setForms(forms.map((form) => (form.id === selectedForm.id ? updatedForm : form)))
      setShowEditForm(false)
      setSelectedForm(null)
      resetForm()
    } catch (error) {
      console.error("Error updating form:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteForm = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this vendor intake form?")) return

    try {
      setLoading(true)
      setForms(forms.filter((form) => form.id !== id))
    } catch (error) {
      console.error("Error deleting form:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleAssignForm = async () => {
    if (!selectedForm || selectedOrganizations.length === 0) return

    try {
      setLoading(true)
      const updatedForm = {
        ...selectedForm,
        assignedOrganizations: [...(selectedForm.assignedOrganizations || []), ...selectedOrganizations],
        totalAssignments: (selectedForm.totalAssignments || 0) + selectedOrganizations.length,
        activeAssignments: (selectedForm.activeAssignments || 0) + selectedOrganizations.length,
        updatedAt: new Date().toISOString(),
      }

      setForms(forms.map((form) => (form.id === selectedForm.id ? updatedForm : form)))
      setShowAssignForm(false)
      setSelectedForm(null)
      setSelectedOrganizations([])
      alert(`Form "${selectedForm.alias}" has been assigned to ${selectedOrganizations.length} organizations.`)
    } catch (error) {
      console.error("Error assigning form:", error)
      alert("Error assigning form. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const toggleFormStatus = async (form: VendorIntakeForm) => {
    try {
      setLoading(true)
      const updatedForm = {
        ...form,
        isActive: !form.isActive,
        updatedAt: new Date().toISOString(),
      }
      setForms(forms.map((f) => (f.id === form.id ? updatedForm : f)))
    } catch (error) {
      console.error("Error updating form status:", error)
    } finally {
      setLoading(false)
    }
  }

  const toggleFormVisibility = async (form: VendorIntakeForm) => {
    try {
      setLoading(true)
      const updatedForm = {
        ...form,
        isHidden: !form.isHidden,
        updatedAt: new Date().toISOString(),
      }
      setForms(forms.map((f) => (f.id === form.id ? updatedForm : f)))
    } catch (error) {
      console.error("Error updating form visibility:", error)
    } finally {
      setLoading(false)
    }
  }

  const openEditForm = (form: VendorIntakeForm) => {
    setSelectedForm(form)
    setNewForm({
      alias: form.alias,
      formstackUrl: form.formstackUrl,
      lawFirmName: form.lawFirmName,
      clientEligibility: form.clientEligibility,
      maxClientsPerIntake: form.maxClientsPerIntake,
      priority: form.priority,
      campaignAssignment: form.campaignAssignment,
      bonusPerQualifiedSale: form.bonusPerQualifiedSale,
    })
    setShowEditForm(true)
  }

  const openAssignForm = (form: VendorIntakeForm) => {
    setSelectedForm(form)
    setSelectedOrganizations([])
    setShowAssignForm(true)
  }

  const resetForm = () => {
    setNewForm({
      alias: "",
      formstackUrl: "",
      lawFirmName: "",
      clientEligibility: "",
      maxClientsPerIntake: 1,
      priority: 1,
      campaignAssignment: "",
      bonusPerQualifiedSale: 0,
    })
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    // You could add a toast notification here
  }

  const renderOrganizationSelector = (orgs: any[], level = 0) => {
    return orgs.map((org) => (
      <div key={org.id} className={`${level > 0 ? "ml-6" : ""}`}>
        <div className="flex items-center p-3 border border-gray-200 rounded-lg mb-2 bg-white">
          <div className="flex items-center flex-1">
            <input
              type="checkbox"
              id={`org-${org.id}`}
              checked={selectedOrganizations.includes(org.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedOrganizations([...selectedOrganizations, org.id])
                } else {
                  setSelectedOrganizations(selectedOrganizations.filter((id) => id !== org.id))
                }
              }}
              disabled={org.type === "BM" || org.type === "AM" || org.type === "M"}
              className="mr-3"
            />
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-600" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{org.name}</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      org.type === "EM"
                        ? "bg-purple-100 text-purple-800"
                        : org.type === "DM"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-green-100 text-green-800"
                    }`}
                  >
                    {org.type}
                  </span>
                  {(org.type === "BM" || org.type === "AM" || org.type === "M") && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex items-center gap-1">
                      <ArrowDown className="w-3 h-3" />
                      Auto-Inherit
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{org.agentCount} agents</p>
              </div>
            </div>
          </div>
        </div>
        {org.children && renderOrganizationSelector(org.children, level + 1)}
      </div>
    ))
  }

  // Filter forms based on search and filters
  const filteredForms = forms.filter((form) => {
    const matchesSearch =
      form.alias.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.lawFirmName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.campaignAssignment.toLowerCase().includes(searchTerm.toLowerCase()) ||
      form.clientEligibility.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && form.isActive) ||
      (statusFilter === "inactive" && !form.isActive)

    const matchesPriority = priorityFilter === "all" || form.priority.toString() === priorityFilter

    return matchesSearch && matchesStatus && matchesPriority
  })

  // Pagination calculations
  const totalItems = filteredForms.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems)
  const currentForms = filteredForms.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2)
      let startPage = Math.max(1, currentPage - halfVisible)
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1)
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i)
      }
    }

    return pages
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Vendor Intake Form Links</h2>
          <p className="text-gray-600">
            Configure and manage vendor intake form links with client eligibility and fulfillment priority
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create Form Link</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Forms Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Forms</p>
              <p className="text-2xl font-bold text-gray-900">{totalItems}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <ExternalLink className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Forms</p>
              <p className="text-2xl font-bold text-gray-900">{filteredForms.filter((f) => f.isActive).length}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Hidden Forms</p>
              <p className="text-2xl font-bold text-gray-900">{filteredForms.filter((f) => f.isHidden).length}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <EyeOff className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Assignments</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredForms.reduce((sum, f) => sum + (f.totalAssignments || 0), 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Priority</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredForms.length > 0
                  ? (filteredForms.reduce((sum, f) => sum + f.priority, 0) / filteredForms.length).toFixed(1)
                  : "0"}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search forms by alias, law firm, campaign, or eligibility..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "all" | "active" | "inactive")}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as "all" | "1" | "2" | "3" | "4")}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Priorities</option>
              <option value="1">Priority 1 (Highest)</option>
              <option value="2">Priority 2</option>
              <option value="3">Priority 3</option>
              <option value="4">Priority 4 (Lowest)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Counter */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          Showing {totalItems > 0 ? startIndex + 1 : 0} to {endIndex} of {totalItems} forms
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-sm text-gray-600">per page</span>
        </div>
      </div>

      {/* Forms Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Form Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Law Firm & Link
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client Eligibility
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Configuration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status & Assignments
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentForms.map((form) => (
                <tr key={form.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900">{form.alias}</p>
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          Priority {form.priority}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{form.campaignAssignment}</p>
                      <p className="text-xs text-gray-400">Bonus: ${form.bonusPerQualifiedSale}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{form.lawFirmName}</p>
                      <p className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded truncate max-w-xs">
                        {form.formstackUrl}
                      </p>
                      <span className="text-xs text-red-600 font-medium">INTERNAL USE ONLY</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <p className="text-sm text-gray-900 line-clamp-3">{form.clientEligibility}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <p className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Max: {form.maxClientsPerIntake} clients
                      </p>
                      <p className="flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        Priority: {form.priority}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          form.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                        }`}
                      >
                        {form.isActive ? "Active" : "Inactive"}
                      </span>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          form.isHidden ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {form.isHidden ? "Hidden" : "Visible"}
                      </span>
                      <span className="text-xs text-gray-600">
                        {form.activeAssignments || 0}/{form.totalAssignments || 0} assignments
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openAssignForm(form)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Assign to Organizations"
                      >
                        <Building2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => window.open(form.formstackUrl, "_blank")}
                        className="text-green-600 hover:text-green-900"
                        title="Open Form"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => copyToClipboard(form.formstackUrl)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Copy URL"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditForm(form)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleFormVisibility(form)}
                        className="text-yellow-600 hover:text-yellow-900"
                        title={form.isHidden ? "Show" : "Hide"}
                      >
                        {form.isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => toggleFormStatus(form)}
                        className={`${form.isActive ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}`}
                        title={form.isActive ? "Deactivate" : "Activate"}
                      >
                        {form.isActive ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDeleteForm(form.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {currentForms.length === 0 && (
          <div className="text-center py-12">
            <ExternalLink className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Vendor Intake Forms Found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || statusFilter !== "all" || priorityFilter !== "all"
                ? "No forms match your current filters. Try adjusting your search criteria."
                : "Create your first vendor intake form link to get started."}
            </p>
            {!searchTerm && statusFilter === "all" && priorityFilter === "all" && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Form Link
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-6 py-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages} ({totalItems} total forms)
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>

              <div className="flex gap-1">
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 text-sm rounded-lg ${
                      currentPage === pageNum ? "bg-blue-600 text-white" : "border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Vendor Intake Form Link</h3>

              <div className="space-y-6">
                {/* Public View Section */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    PUBLIC VIEW FOR SALES REPS
                  </h4>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">
                      Link Alias (What sales reps will see)
                    </label>
                    <input
                      type="text"
                      value={newForm.alias}
                      onChange={(e) => setNewForm({ ...newForm, alias: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Mass Tort Gaming Addiction A"
                    />
                    <p className="text-xs text-blue-600 mt-1">
                      This is the only information visible to sales representatives
                    </p>
                  </div>
                </div>

                {/* Internal Use Section */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-medium text-red-900 mb-3 flex items-center gap-2">
                    <EyeOff className="w-5 h-5" />
                    INTERNAL USE ONLY
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-red-800 mb-1">Law Firm Name</label>
                      <input
                        type="text"
                        value={newForm.lawFirmName}
                        onChange={(e) => setNewForm({ ...newForm, lawFirmName: e.target.value })}
                        className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="e.g., Smith & Associates Law Firm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-red-800 mb-1">Formstack URL</label>
                      <input
                        type="url"
                        value={newForm.formstackUrl}
                        onChange={(e) => setNewForm({ ...newForm, formstackUrl: e.target.value })}
                        className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="https://formstack.com/forms/..."
                      />
                    </div>
                  </div>
                  <p className="text-xs text-red-600 mt-2">
                    This information is confidential and not visible to sales representatives
                  </p>
                </div>

                {/* Client Eligibility Section */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    CLIENT ELIGIBILITY CRITERIA
                  </h4>
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">
                      Client Eligibility (Age, Game System, Qualifying Titles, etc.)
                    </label>
                    <textarea
                      value={newForm.clientEligibility}
                      onChange={(e) => setNewForm({ ...newForm, clientEligibility: e.target.value })}
                      className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={4}
                      placeholder="e.g., Ages 18-65, PlayStation/Xbox/PC, FIFA/Madden/NBA2K series, minimum 2 hours daily play, recent purchases within 12 months"
                    />
                    <p className="text-xs text-green-600 mt-1">
                      Specify detailed eligibility requirements including age ranges, gaming systems, qualifying game
                      titles, play time, purchase history, etc.
                    </p>
                  </div>
                </div>

                {/* Configuration Section */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-medium text-purple-900 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    FULFILLMENT CONFIGURATION
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-purple-800 mb-1">Max Clients Per Intake</label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={newForm.maxClientsPerIntake}
                        onChange={(e) =>
                          setNewForm({ ...newForm, maxClientsPerIntake: Number.parseInt(e.target.value) })
                        }
                        className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-purple-600 mt-1">Maximum number of clients per intake session</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-purple-800 mb-1">Priority of Fulfillment</label>
                      <select
                        value={newForm.priority}
                        onChange={(e) => setNewForm({ ...newForm, priority: Number.parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value={1}>1 - Highest Priority</option>
                        <option value={2}>2 - High Priority</option>
                        <option value={3}>3 - Medium Priority</option>
                        <option value={4}>4 - Low Priority</option>
                      </select>
                      <p className="text-xs text-purple-600 mt-1">Lower numbers = higher priority</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-purple-800 mb-1">Bonus Per Qualified Sale</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newForm.bonusPerQualifiedSale}
                        onChange={(e) =>
                          setNewForm({ ...newForm, bonusPerQualifiedSale: Number.parseFloat(e.target.value) })
                        }
                        className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-purple-600 mt-1">Additional bonus amount per qualified sale</p>
                    </div>
                  </div>
                </div>

                {/* Campaign Assignment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Assignment</label>
                  <input
                    type="text"
                    value={newForm.campaignAssignment}
                    onChange={(e) => setNewForm({ ...newForm, campaignAssignment: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Mass Tort Gaming Campaign 2024"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateForm}
                  disabled={
                    !newForm.alias.trim() ||
                    !newForm.formstackUrl.trim() ||
                    !newForm.lawFirmName.trim() ||
                    !newForm.clientEligibility.trim()
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Create Form Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {showEditForm && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Vendor Intake Form Link</h3>

              <div className="space-y-6">
                {/* Public View Section */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    PUBLIC VIEW FOR SALES REPS
                  </h4>
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">
                      Link Alias (What sales reps will see)
                    </label>
                    <input
                      type="text"
                      value={newForm.alias}
                      onChange={(e) => setNewForm({ ...newForm, alias: e.target.value })}
                      className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Internal Use Section */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-medium text-red-900 mb-3 flex items-center gap-2">
                    <EyeOff className="w-5 h-5" />
                    INTERNAL USE ONLY
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-red-800 mb-1">Law Firm Name</label>
                      <input
                        type="text"
                        value={newForm.lawFirmName}
                        onChange={(e) => setNewForm({ ...newForm, lawFirmName: e.target.value })}
                        className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-red-800 mb-1">Formstack URL</label>
                      <input
                        type="url"
                        value={newForm.formstackUrl}
                        onChange={(e) => setNewForm({ ...newForm, formstackUrl: e.target.value })}
                        className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Client Eligibility Section */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    CLIENT ELIGIBILITY CRITERIA
                  </h4>
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-1">
                      Client Eligibility (Age, Game System, Qualifying Titles, etc.)
                    </label>
                    <textarea
                      value={newForm.clientEligibility}
                      onChange={(e) => setNewForm({ ...newForm, clientEligibility: e.target.value })}
                      className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={4}
                    />
                  </div>
                </div>

                {/* Configuration Section */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-medium text-purple-900 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    FULFILLMENT CONFIGURATION
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-purple-800 mb-1">Max Clients Per Intake</label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={newForm.maxClientsPerIntake}
                        onChange={(e) =>
                          setNewForm({ ...newForm, maxClientsPerIntake: Number.parseInt(e.target.value) })
                        }
                        className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-purple-800 mb-1">Priority of Fulfillment</label>
                      <select
                        value={newForm.priority}
                        onChange={(e) => setNewForm({ ...newForm, priority: Number.parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value={1}>1 - Highest Priority</option>
                        <option value={2}>2 - High Priority</option>
                        <option value={3}>3 - Medium Priority</option>
                        <option value={4}>4 - Low Priority</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-purple-800 mb-1">Bonus Per Qualified Sale</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newForm.bonusPerQualifiedSale}
                        onChange={(e) =>
                          setNewForm({ ...newForm, bonusPerQualifiedSale: Number.parseFloat(e.target.value) })
                        }
                        className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Campaign Assignment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Assignment</label>
                  <input
                    type="text"
                    value={newForm.campaignAssignment}
                    onChange={(e) => setNewForm({ ...newForm, campaignAssignment: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowEditForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditForm}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Form Modal */}
      {showAssignForm && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Assign "{selectedForm.alias}" to Organizations
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Organization Selector */}
                <div className="lg:col-span-2">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Select Organizations (EM, DM, CM levels only)
                  </h4>
                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                    {renderOrganizationSelector(organizations)}
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <h5 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                      <GitBranch className="w-4 h-4" />
                      Assignment Rules
                    </h5>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Only EM, DM, CM levels can be directly assigned</li>
                      <li>• BM, AM, M levels automatically inherit from their parent CM</li>
                      <li>• Inheritance is immediate and automatic</li>
                      <li>• All affected users will have access to this form</li>
                    </ul>
                  </div>
                </div>

                {/* Assignment Preview */}
                <div className="lg:col-span-1">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Assignment Preview
                  </h4>

                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h5 className="font-medium text-gray-900 mb-3">Impact Summary</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Direct Users:</span>
                          <span className="font-medium">{assignmentPreview.directUsers}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Inherited Users:</span>
                          <span className="font-medium">{assignmentPreview.inheritedUsers}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-300">
                          <span className="font-medium">Total Affected:</span>
                          <span className="font-bold text-blue-600">{assignmentPreview.totalUsers}</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-green-50 rounded-lg">
                      <h5 className="font-medium text-green-900 mb-3">Selected Organizations</h5>
                      <div className="space-y-1 text-sm text-green-800 max-h-32 overflow-y-auto">
                        {selectedOrganizations.length === 0 ? (
                          <p className="text-green-600">No organizations selected</p>
                        ) : (
                          selectedOrganizations.map((orgId) => {
                            const org = organizations
                              .flatMap((o) => [o, ...(o.children || []).flatMap((c) => [c, ...(c.children || [])])])
                              .find((o) => o.id === orgId)
                            return (
                              <div key={orgId} className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                                <span>{org?.name}</span>
                                <span className="text-xs">({org?.type})</span>
                              </div>
                            )
                          })
                        )}
                      </div>
                    </div>

                    {assignmentPreview.affectedOrganizations.length > selectedOrganizations.length && (
                      <div className="p-4 bg-yellow-50 rounded-lg">
                        <h5 className="font-medium text-yellow-900 mb-3 flex items-center gap-2">
                          <ArrowDown className="w-4 h-4" />
                          Auto-Inherited Organizations
                        </h5>
                        <div className="space-y-1 text-sm text-yellow-800 max-h-32 overflow-y-auto">
                          {assignmentPreview.affectedOrganizations
                            .filter(
                              (orgName) =>
                                !selectedOrganizations.some((orgId) => {
                                  const org = organizations
                                    .flatMap((o) => [
                                      o,
                                      ...(o.children || []).flatMap((c) => [c, ...(c.children || [])]),
                                    ])
                                    .find((o) => o.id === orgId)
                                  return org?.name === orgName
                                }),
                            )
                            .map((orgName, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <ArrowDown className="w-3 h-3 text-yellow-600" />
                                <span>{orgName}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowAssignForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignForm}
                  disabled={selectedOrganizations.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Assign Form ({selectedOrganizations.length} organizations)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
